/**
 * Per-technician ranking table: avatar + name + services count (with
 * inline bar) + avg rating + reviews count. Header columns can be
 * clicked to re-sort. The avg-rating column is the default sort.
 */
import { useMemo, useState } from "react";

import ChartCard from "../ChartCard/ChartCard";
import Stars from "../Stars/Stars";
import {
  avatarBg,
  formatRating
} from "../../../../../constants/analytics.constants";
import { TechnicianRankingStyles } from "./technicianRanking.styles";

const TechnicianRanking = ({ data = [] }) => {
  const [sortBy, setSortBy] = useState("avgGrade");

  const sorted = useMemo(() => {
    const copy = [...data];
    copy.sort((a, b) => {
      const aVal = a[sortBy] ?? -Infinity;
      const bVal = b[sortBy] ?? -Infinity;
      return bVal - aVal;
    });
    return copy;
  }, [data, sortBy]);

  const max = Math.max(1, ...data.map(d => d.servicesCount || 0));

  return (
    <ChartCard
      title="Technician ranking"
      subtitle="Click a column header to re-sort"
      padded={false}
    >
      {data.length === 0 ? (
        <div style={{ padding: 32, textAlign: "center", color: "#8E8E8E", fontSize: 12 }}>
          No technician data in this range
        </div>
      ) : (
        <TechnicianRankingStyles>
          <div className="rank-head">
            <div>#</div>
            <div>Technician</div>
            <div
              className={`sortable ${sortBy === "servicesCount" ? "is-active" : ""}`}
              onClick={() => setSortBy("servicesCount")}
            >
              Services
            </div>
            <div
              className={`sortable ${sortBy === "avgGrade" ? "is-active" : ""}`}
              onClick={() => setSortBy("avgGrade")}
            >
              Avg rating
            </div>
            <div>Reviews</div>
          </div>
          {sorted.map((t, i) => (
            <div key={t.employeeId} className="rank-row">
              <div className="rank-pos">{i + 1}</div>
              <div className="rank-tech">
                <div
                  className="rank-avatar"
                  style={{ background: avatarBg(t.firstName + t.lastName) }}
                >
                  {(t.firstName?.[0] || "").toUpperCase()}
                  {(t.lastName?.[0] || "").toUpperCase()}
                </div>
                <div>
                  <div className="rank-name">
                    {t.firstName} {t.lastName}
                  </div>
                  <div className="rank-meta">ID: {t.employeeId}</div>
                </div>
              </div>
              <div>
                <div className="rank-bar-row">
                  <div className="rank-bar-track">
                    <div
                      className="rank-bar-fill"
                      style={{ width: `${(t.servicesCount / max) * 100}%` }}
                    />
                  </div>
                  <span className="rank-num">{t.servicesCount}</span>
                </div>
              </div>
              <div className="rank-rating">
                <span className="rank-rating-val">
                  {t.avgGrade !== null && t.avgGrade !== undefined
                    ? formatRating(t.avgGrade)
                    : "—"}
                </span>
                {t.avgGrade !== null && t.avgGrade !== undefined && (
                  <Stars value={t.avgGrade} size={11} />
                )}
              </div>
              <div className="rank-reviews">{t.reviewsCount || 0}</div>
            </div>
          ))}
        </TechnicianRankingStyles>
      )}
    </ChartCard>
  );
};

export default TechnicianRanking;
